import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@material-ui/core";
import {
  HaklaiStyledHeaderCell as StyledHeaderCell,
  HaklaiStyledNameHeaderCell as StyledNameHeaderCell
} from "../../shared/table/haklaiTableShared";
import useHaklaiTableFit from "../../shared/table/useHaklaiTableFit";
import HaklaiSectionLabelCell, {
  HAKLAI_SECTION_LABEL_COLUMN_WIDTH
} from "../../shared/ui/HaklaiSectionLabelCell";
import {
  ACTION_COLUMN_WIDTH,
  DEPRECIATION_COLUMN_WIDTH,
  PTOR_COLUMN_WIDTH,
  PROPERTY_PART_NAME_COLUMN_WIDTH,
  PRE1997_ZABAN_COLUMN_WIDTH,
  STANDARD_VALUE_COLUMN_WIDTH,
  StyledGroupCell
} from "../haklaiPropertyPartsStyles";
import {
  HAKLAI_PROPERTY_PART_LABELS,
  HAKLAI_PROPERTY_PART_TOOLTIPS
} from "../haklaiPropertyPartsTexts";

const CELL_HORIZONTAL_PADDING = 8;
const PROPERTY_PARTS_TABLE_BASE_MIN_WIDTH = PROPERTY_PART_NAME_COLUMN_WIDTH
  + HAKLAI_SECTION_LABEL_COLUMN_WIDTH
  + STANDARD_VALUE_COLUMN_WIDTH
  + PTOR_COLUMN_WIDTH
  + ACTION_COLUMN_WIDTH;

const getZabanColumnWidth = (hasPre1997ConstructionQuestion) => (
  hasPre1997ConstructionQuestion ? PRE1997_ZABAN_COLUMN_WIDTH : STANDARD_VALUE_COLUMN_WIDTH
);

const getPropertyPartsTableMinWidth = ({
  showZabanColumn,
  hasPre1997ConstructionQuestion
}) => (
  PROPERTY_PARTS_TABLE_BASE_MIN_WIDTH
  + (showZabanColumn ? getZabanColumnWidth(hasPre1997ConstructionQuestion) : 0)
  + DEPRECIATION_COLUMN_WIDTH
);

const getPropertyPartsColumnCount = ({ showZabanColumn }) => (
  6
  + (showZabanColumn ? 1 : 0)
);

export const getPropertyPartsTableWidth = ({
  showZabanColumn,
  hasPre1997ConstructionQuestion
}) => (
  getPropertyPartsTableMinWidth({
    showZabanColumn,
    hasPre1997ConstructionQuestion
  }) + (getPropertyPartsColumnCount({ showZabanColumn }) * CELL_HORIZONTAL_PADDING)
);

const HeaderLabel = ({ classes, children, tooltip }) => {
  const label = (
    <Typography component="span" className={classes.headerLabelText}>
      {children}
    </Typography>
  );
  if (!tooltip) return label;
  return <Tooltip title={tooltip}>{label}</Tooltip>;
};

const HaklaiPropertySection = React.memo(function HaklaiPropertySection({
  classes,
  sectionId,
  sectionTitle,
  addMode,
  addAriaLabel,
  onAddClick,
  showAddButton = true,
  bodies,
  renderRow,
  showZabanColumn
}) {
  const columnCount = getPropertyPartsColumnCount({ showZabanColumn });

  const sectionAddButton = showAddButton ? {
    classes,
    mode: addMode,
    onClick: onAddClick,
    label: addAriaLabel
  } : null;

  return (
    <TableBody>
      {bodies.map((body, bodyIndex) => {
        const rows = body.rows || [];
        if (rows.length === 0) {
          return (
            <TableRow key={`${sectionId}_${body.bodyKey}_empty`}>
              <HaklaiSectionLabelCell
                title={bodyIndex === 0 ? sectionTitle : ""}
                addButton={bodyIndex === 0 ? sectionAddButton : null}
              />
              <StyledGroupCell colSpan={columnCount - 1} className={classes.emptyStateCell}>
                <Typography component="span" className={classes.emptyStateText}>
                  {HAKLAI_PROPERTY_PART_LABELS.emptySection}
                </Typography>
              </StyledGroupCell>
            </TableRow>
          );
        }

        return (
          <React.Fragment key={`${sectionId}_${body.bodyKey}`}>
            {rows.map(([rowKey, part], rowIndex) => {
              const isSectionStart = bodyIndex === 0 && rowIndex === 0;
              return renderRow(rowKey, part, {
                subSectionLabel: body.subSectionLabel,
                subSectionRowSpan: rows.length,
                isFirstInSubSection: rowIndex === 0,
                isLastInBody: rowIndex === rows.length - 1,
                showAddButton: showAddButton && isSectionStart,
                sectionLabel: isSectionStart ? sectionTitle : "",
                addMode,
                addAriaLabel,
                onAddClick
              });
            })}
          </React.Fragment>
        );
      })}
    </TableBody>
  );
});

const columnBox = (width) => ({ width, minWidth: width, maxWidth: width });

const HaklaiPropertyPartsTable = ({
  classes,
  stickyHeaderEnabled,
  showZabanColumn,
  hasPre1997ConstructionQuestion,
  nameColumnWidth = PROPERTY_PART_NAME_COLUMN_WIDTH,
  columnWidths = null,
  tableWidth: tableWidthOverride = null,
  sections
}) => {
  const zabanColumnWidth = columnWidths?.zaban ?? getZabanColumnWidth(hasPre1997ConstructionQuestion);
  const resolvedNameColumnWidth = columnWidths?.name ?? nameColumnWidth;
  const ptorColumnWidth = columnWidths?.ptor ?? PTOR_COLUMN_WIDTH;
  const valueColumnWidth = columnWidths?.value ?? STANDARD_VALUE_COLUMN_WIDTH;
  const depreciationColumnWidth = columnWidths?.depreciation ?? DEPRECIATION_COLUMN_WIDTH;
  const tableWidth = tableWidthOverride ?? (
    getPropertyPartsTableWidth({
      showZabanColumn,
      hasPre1997ConstructionQuestion
    }) + (resolvedNameColumnWidth - PROPERTY_PART_NAME_COLUMN_WIDTH)
  );
  const tableFrameRef = React.useRef(null);
  const isTableFit = useHaklaiTableFit(tableFrameRef, tableWidth);

  return (
    <Box
      ref={tableFrameRef}
      className={[
        "haklai-data-table",
        "haklai-calc-table",
        isTableFit ? "is-fit-to-width" : "",
        classes.tableFrameAlign
      ].filter(Boolean).join(" ")}
    >
      <TableContainer className={classes.tableWrapper}>
        <Table
          size="small"
          className={classes.partsTable}
          style={{
            tableLayout: "fixed",
            width: tableWidth,
            minWidth: tableWidth
          }}
        >
          <TableHead
            className={[
              classes.stickyHeader,
              (!stickyHeaderEnabled || isTableFit) ? classes.stickyHeaderDisabled : ""
            ].filter(Boolean).join(" ")}
          >
            <TableRow>
              <StyledHeaderCell
                className="haklai-section-label-cell"
                style={columnBox(HAKLAI_SECTION_LABEL_COLUMN_WIDTH)}
              />
              <StyledNameHeaderCell
                style={columnBox(resolvedNameColumnWidth)}
              >
                <HeaderLabel classes={classes}>
                  {HAKLAI_PROPERTY_PART_LABELS.farmAssets}
                </HeaderLabel>
              </StyledNameHeaderCell>

              <StyledHeaderCell
                style={columnBox(ptorColumnWidth)}
                aria-label={HAKLAI_PROPERTY_PART_LABELS.requestedPtor}
              >
                <HeaderLabel classes={classes} tooltip={HAKLAI_PROPERTY_PART_TOOLTIPS.requestedPtorForPart}>
                  {HAKLAI_PROPERTY_PART_LABELS.requestedPtor}
                </HeaderLabel>
              </StyledHeaderCell>

              <StyledHeaderCell
                style={columnBox(valueColumnWidth)}
              >
                <HeaderLabel classes={classes}>
                  {HAKLAI_PROPERTY_PART_LABELS.relativeSaleValue}
                </HeaderLabel>
              </StyledHeaderCell>

              {showZabanColumn ? (
                <StyledHeaderCell
                  style={columnBox(zabanColumnWidth)}
                >
                  <HeaderLabel
                    classes={classes}
                    tooltip={HAKLAI_PROPERTY_PART_TOOLTIPS.zabanValueForResidence}
                  >
                    {HAKLAI_PROPERTY_PART_LABELS.zabanValue}
                  </HeaderLabel>
                </StyledHeaderCell>
              ) : null}

              <StyledHeaderCell
                style={columnBox(depreciationColumnWidth)}
                aria-label={HAKLAI_PROPERTY_PART_LABELS.rentalDepreciation}
              >
                <HeaderLabel classes={classes} tooltip={HAKLAI_PROPERTY_PART_TOOLTIPS.rentalDepreciationForPart}>
                  {HAKLAI_PROPERTY_PART_LABELS.rentalDepreciation}
                </HeaderLabel>
              </StyledHeaderCell>

              <StyledHeaderCell
                className="haklai-icon-cell"
                style={columnBox(ACTION_COLUMN_WIDTH)}
              />
            </TableRow>
          </TableHead>

          {sections.map((section) => (
            <HaklaiPropertySection
              key={section.sectionId}
              classes={classes}
              sectionId={section.sectionId}
              sectionTitle={section.sectionTitle}
              addMode={section.addMode}
              addAriaLabel={section.addAriaLabel}
              onAddClick={section.onAddClick}
              showAddButton={section.showAddButton !== false}
              bodies={section.bodies}
              renderRow={section.renderRow}
              showZabanColumn={showZabanColumn}
            />
          ))}
        </Table>
      </TableContainer>
    </Box>
  );
};

export default React.memo(HaklaiPropertyPartsTable);
